'use client';

import { Base64Utils } from '@/lib/helpers/base64';
import { buildProxyUrl } from '@/lib/helpers/image-proxy';
import { cn } from '@/lib/utils';
import { useEffect, useState } from 'react';

interface IProxyImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  onClick?: () => void;
}

export function ProxyImage({ src, alt, width, height, className, onClick }: IProxyImageProps) {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    // Skip proxy for local images
    if (src.startsWith('/images/')) {
      setImageSrc(src);
      setIsLoading(false);
      return;
    }

    const loadImage = async () => {
      try {
        setIsLoading(true);
        
        // src is already Base64-encoded from backend
        // Use it directly as the URL parameter for proxy API
        const apiUrl = buildProxyUrl(src);
        
        const response = await fetch(apiUrl, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
          },
        });
        if (!response.ok) throw new Error('Failed to load image');

        const data = await response.json();
        setImageSrc(data.dataUrl);
        setError(false);
      } catch (err) {
        console.error('Error loading image:', err);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadImage();
  }, [src]);

  if (isLoading || !imageSrc) {
    return (
      <div 
        className={cn("animate-pulse rounded bg-gray-200", className)} 
        style={{ width, height }} 
      />
    );
  }

  if (error) {
    return (
      <div 
        className={cn("flex items-center justify-center rounded bg-gray-100", className)} 
        style={{ width, height }}
      >
        Error
      </div>
    );
  }

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Image click is optional and decorative
    <img
      src={imageSrc}
      alt={alt}
      width={width}
      height={height}
      className={className}
      onClick={onClick}
      loading="lazy"
    />
  );
} 